import React, { useRef, useEffect, useContext } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import { mapContext } from '../Services/MapContext';
import { mapTimes } from '../Utilities/Helpers';
import {
	POINT_LAYER_SRC,
	POINT_LAYER_HIDDEN_CLUSTERS,
	MAX_CLUSTER_ZOOM,
	SPIDER_CIRCUMFERENCE,
	DEFAULT_COORDS,
	DEFAULT_ZOOM,
	MAPBOX_TOKEN
} from '../Utilities/Config';

import countryBorders from '../Data/countries_dissolved_simple.geojson';

import paperPatternImage from '../Images/map-paper-pattern.jpg';
import patternImage from '../Images/map-putty-pattern.jpg';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapMapbox.css';

// left to right texts
mapboxgl.setRTLTextPlugin(
	'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
	null,
	true // Lazy load the plugin
);

const Map = (props) => {
	const {
		data,
		activeCategories,
		selectedData,
		setSelectedData,
		toggleSidebar,
		sidebarWidth
	} = props;

	mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_KEY || MAPBOX_TOKEN;

	const {
		map,
		setMap,
		mapLoaded,
		setMapLoaded,
		layersLoaded,
		setLayersLoaded
	} = useContext(mapContext);

	const selectedDataPrev = useRef(null);
	const mapContainer = useRef(null);

	// objects for caching and keeping track of HTML marker objects (for performance)
	const mapMarkers = useRef({});
	const temporaryClusterMarkers = useRef([]);
	const mapClusterMarkers = useRef({});
	const markersOnScreen = useRef({ clusters: {}, markers: {} });
	const clickedCluster = useRef(null);

	let sidebarWidthLocal = useRef(sidebarWidth || 0);
	
	const toggleLayers = (categories) => {
		if (map && categories) {
			const filterCategories = Object.values(categories).map(el => (el.state) ? parseInt(el.id) : null).filter(el=>el);
			addPointLayer(data, filterCategories);
		}
	}

	// add point layer
	const addPointLayer = async (fullData, filter) => {
		// remove all old clusters & markers
		if (Object.keys(mapClusterMarkers.current).length) {
			Object.entries(mapClusterMarkers.current).forEach(([key, cluster]) => {
				cluster.remove();
				delete mapClusterMarkers.current[key];
			});
		}
		if (Object.keys(mapMarkers.current).length) {
			Object.entries(mapMarkers.current).forEach(([key, marker]) => {
				marker.remove();
				delete mapMarkers.current[key];
			});
		}
		removeSpiderifiedMarkers();

		// unbind previous map.render
		map.off('render', updateClustersOnMapRender);

		// filter features by category (subject id)
		const features = filter ? [...fullData.features].map(el => {
			return (el.properties.subjectIDs.some(id => filter.includes(id))) ? el : null;
		}).filter(el=>el) : [...fullData.features];
		const geojson = { type: 'FeatureCollection', features };

		// keep separate counts for each category in a cluster
		let clusterProperties = Object.entries(activeCategories).reduce((acc, [key, val]) => {
			acc[key] = ['+', ['case', ['in', parseInt(key), ['get', 'subjectIDs']], 1, 0]];
			return acc;
		}, {});

		// save feature count
		clusterProperties.featureCount = [
			"+",
			1,
			["reduce", "+", 0,
				["case",
					[">", ["length", ["filter", ">", ["length", ["get", "subjectIDs"]], 6]], 1, 0]
				]
			]
		];

		if (map.getSource(POINT_LAYER_SRC)) {
			map.getSource(POINT_LAYER_SRC).setData(geojson);
		} else {
			map.addSource(POINT_LAYER_SRC, {
				'type': 'geojson',
				'data': geojson,
				'cluster': true,
				'clusterRadius': 5,
				'clusterProperties': clusterProperties
			});
		}

		// color differently, according to our db
		let circleColorProps = ['case'];
		Object.entries(activeCategories).forEach(([key, val]) => {
			circleColorProps.push(['in', parseInt(key), ['get', 'subjectIDs']]);
			circleColorProps.push(val.color);
		});
		circleColorProps.push('transparent');

		// hidden cluster layer for zooming and interacting
		if (!map.getLayer(POINT_LAYER_HIDDEN_CLUSTERS)) {
			map.addLayer({
				id: POINT_LAYER_HIDDEN_CLUSTERS,
				type: 'circle',
				source: POINT_LAYER_SRC,
				filter: ['has', 'point_count'],
				paint: {
					'circle-color': 'transparent',
					'circle-radius': 0
				}
			});
		}
		
		// after the GeoJSON data is loaded, update markers on the screen on every frame
		updateClustersOnMapRender();
		map.on('render', updateClustersOnMapRender);
	}

	// add custom overlay
	const addOverlayLayer = () => {

		const polyPatternName = 'paperPattern';
		const overlayPatternName = 'overlayPattern';

		const polySourceName = 'borders_src';
		const polyLayerName = 'borders_layer';

		const overlaySourceName = 'overlay_src';
		const overlayLayerName = 'overlay_layer';
		const wholeWorldGeoJSON = {
			"type": "Feature",
			"properties": {},
			"geometry": {
				"coordinates": [[
					[-180.0, 90.0],
					[-180.0, -90.0],
					[180.0, -90.0],
					[180.0, 90.0],
					[-180.0, 90.0]
				]],
				"type": "Polygon"
			}
		};

		const promises = [
			{img: paperPatternImage, name: polyPatternName},
			{img: patternImage, name: overlayPatternName}
		].map((el => {
			return new Promise((resolve, reject) => {
				map.loadImage(el.img, (err, image) => {
					if (err) reject(err);
					map.addImage(el.name, image);
					resolve(image);
				});
			}); 
		}));

		Promise.all(promises).then(() => {
			// full-screen overlay
			map.addSource(overlaySourceName, {
				'type': 'geojson',
				'data': wholeWorldGeoJSON
			});
			map.addLayer({
				'id': overlayLayerName,
				'type': 'fill',
				'source': overlaySourceName,
				'paint': {
					'fill-opacity': 0.3,
					'fill-pattern': overlayPatternName
				}
			});

			// country borders with pattern-fill
			map.addSource(polySourceName, {
				'type': 'geojson',
				'data': countryBorders
			});
			map.addLayer({
				'id': polyLayerName,
				'type': 'fill',
				'source': polySourceName,
				'paint': {
					'fill-opacity': 1,
					'fill-pattern': polyPatternName
				}
			}, 'settlement-minor-label');
		});
	};

	// update clusters func
	const updateClustersOnMapRender = () => {
		if (!map.isSourceLoaded(POINT_LAYER_SRC)) return;

		const newMarkers = { clusters: {}, markers: {} };
		const features = map.querySourceFeatures(POINT_LAYER_SRC);

		// create an HTML marker for every cluster on the screen
		for (const feature of features) {
			const coords = feature.geometry.coordinates;
			const props = feature.properties;

			let id = (props.cluster) ? props.cluster_id : props.ID;
			let storage = (props.cluster) ? mapClusterMarkers : mapMarkers;

			let marker = storage.current[id];
			if (!marker) {
				const el = createChart(props);
				marker = new mapboxgl.Marker({
					element: el,
					anchor: (props.cluster) ? 'center' : 'bottom',
					offset: (props.cluster) ? [0, 0] : [0, -5],
				}).setLngLat(coords);
				storage.current[id] = marker;
			}

			newMarkers[props.cluster ? 'clusters' : 'markers'][id] = marker;
			//if (!markersOnScreen.current[props.cluster ? 'clusters' : 'markers'][id]) marker.addTo(map);
			marker.addTo(map);
		}

		// remove markers which are not visible
		for (const group in markersOnScreen.current) {
			for (const id in markersOnScreen.current[group]) {
				if (!newMarkers[group][id]) {
					markersOnScreen.current[group][id].remove();
					delete markersOnScreen.current[group][id];
				}
			}
		}
		markersOnScreen.current = newMarkers;
	}

	// show sidebar on marker click
	useEffect(() => {
		if (mapLoaded && layersLoaded) {
			clickOnMarker(selectedData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapLoaded, layersLoaded, selectedData]);

	// generate doughnut
	// code for creating an SVG donut chart from feature properties
	const createChart = (props, spiderOffset, extraClass) => {
		const offsets = [];
		const counts = (props.cluster)
			? Object.keys(activeCategories).map(id => props[id])
			: Object.values(activeCategories).map(el => {
				let ids = props.subjectIDs
				if (typeof(props.subjectIDs) === 'string') {
					ids = JSON.parse(props.subjectIDs);
				}
				return ids.includes(parseInt(el.id)) ? 1 : 0;
			});

		//const colors = Object.values(activeCategories).map(el => el.color);
		//const numberOfColours = counts.filter(c => c !== 0).length;

		let html = '';
		let total = 0;
		for (const count of counts) {
			offsets.push(total);
			total += count;
		}

		/*
		const r = (props.cluster) ? 22 : 14; // chart radius (outer ring size)
		const chartInnerSize = (props.cluster) ? 0.75 : 0.6; // chart weight (inner ring size)
		const r0 = Math.round(r * chartInnerSize);
		const w = r * 2; // width of the colored circle-part
		*/

		//if (props.cluster && (numberOfColours > 4)) {
		if (props.cluster) {
			html = `
				<div>
					<div class="marker-cluster-gradient">
						<div class="marker-cluster-content">${props.point_count.toLocaleString()}</div>
					</div>
				</div>
			`.trim();
		} else {
			let markerStyle = `background-image:url(${props.marker_thumbnail});`;
			if (spiderOffset) {
				const angle = parseFloat(spiderOffset.angle) * -1;
				markerStyle += `transform:rotateZ(${angle}deg);`;
			}
			html = `<div class="marker-icon" style="${markerStyle}"></div>`
			/*
			html = `<div>
			<svg width="${w}" height="${w}" viewbox="0 0 ${w} ${w}" text-anchor="middle">`;
			
			for (let i = 0; i < counts.length; i++) {
				html += donutSegment(
					offsets[i] / total,
					(offsets[i] + counts[i]) / total,
					r,
					r0,
					colors[i]
				);
			}
			html += `
				<circle cx="${r}" cy="${r}" r="${r0}" fill="white" />
				<text dominant-baseline="central" transform="translate(${r}, ${r})">
					${(props.point_count) ? props.point_count.toLocaleString() : ''}
				</text>
				</svg>
				</div>
			`;
			*/
		}

		const el = document.createElement('div');
		el.innerHTML = html;
		el.className = (props.point_count) ? 'mapboxgl-marker-cluster' : 'custom-marker';
		if (extraClass) el.className = `${el.className} ${extraClass}`;
		el.onclick = (e) => { handleChartClick(e, props) };

		if (spiderOffset) {
			el.style.marginLeft = `${spiderOffset.x}px`;
			el.style.marginTop = `calc(${spiderOffset.y}px + 20px)`;
		}
		return el;
	}

	/*
	const donutSegment = (start, end, r, r0, color) => {
		if (end - start === 1) end -= 0.00001;
		const a0 = 2 * Math.PI * (start - 0.25);
		const a1 = 2 * Math.PI * (end - 0.25);
		const x0 = Math.cos(a0);
		const y0 = Math.sin(a0);
		const x1 = Math.cos(a1);
		const y1 = Math.sin(a1);
		const largeArc = end - start > 0.5 ? 1 : 0;
		
		// draw an SVG path
		return `<path d="M ${r + r0 * x0} ${r + r0 * y0} L ${r + r * x0} ${
		r + r * y0
		} A ${r} ${r} 0 ${largeArc} 1 ${r + r * x1} ${r + r * y1} L ${
		r + r0 * x1
		} ${r + r0 * y1} A ${r0} ${r0} 0 ${largeArc} 0 ${r + r0 * x0} ${
		r + r0 * y0
		}" fill="${color}" />`;
	}
	*/

	const clickOnMarker = (feature) => {
		if (!feature) return;

		// Open sidebar
		toggleSidebar(true);
		

		/*
		if (feature) {
			const props = feature.properties;

			// Center on Marker
			// get coordinates
			let center;
			if (feature.geometry.type === 'Point') {
				center = feature.geometry.coordinates; // get coordinates
			}
	
			// Fly to marker
			if (isMobile()) {
				map.flyTo({ center: center });
			} else {
				let x_offset = sidebarWidthLocal.current;
				center = map.project(center);
				let target = map.unproject([center.x + (x_offset/2), center.y]);
				map.flyTo({ center: target });
			}

			// Open sidebar
			toggleSidebar(true);
	
			// Update URL with new ID
			if (props.id) {
				setSearchParams(params => {
					params.set('id', props.id);
					return params;
				});
			}
		}
		*/
	}

	// when user clicks on the donut chart marker (cluster or normal marker)
	const handleChartClick = (e, props) => {

		// remove previous spiderified markers if they are visible
		removeSpiderifiedMarkers();

		// proceed with the click event
		if (props.cluster) {
			const features = map.queryRenderedFeatures(e.point, {
				layers: [POINT_LAYER_HIDDEN_CLUSTERS]
			}).filter(el => el.id === props.cluster_id);

			const clusterSource = map.getSource(POINT_LAYER_SRC);
			clusterSource.getClusterExpansionZoom(props.cluster_id, (err, zoom) => {
				if (err) return;
				if (zoom === 2 || zoom === 5) zoom += 1;
				if (zoom >= MAX_CLUSTER_ZOOM) {
					clusterSource.getClusterChildren(props.cluster_id, (error, children) => {
						if (error) return;
						//console.log('props', props)
						//console.log('Cluster children:', children);

						const clusterCoordinates = features[0].geometry.coordinates;
						const spiderLegParams = generateCircleParams(children.length);

						// add opacity to currently selected spiderifyable cluster
						const currentClusterFeature = markersOnScreen.current.clusters[props.cluster_id];
						const currentClusterElement = currentClusterFeature._element;
						clickedCluster.current = currentClusterElement;
						clickedCluster.current.classList.add('halfopacity');

						// create and show temporary markers
						children.forEach((feature, index) => {
							// TODO: rewrite
							const el = createChart(feature.properties, spiderLegParams[index], 'spiderified');
							const marker = new mapboxgl.Marker({
								element: el,
								anchor: 'bottom',
								offset: [0, -5],
								rotation: spiderLegParams[index].angle
							});
							marker.setLngLat(clusterCoordinates);
							marker.addTo(map);

							temporaryClusterMarkers.current.push(marker);
						});
					});

				} else {
					map.easeTo({
						center: features[0].geometry.coordinates,
						zoom: zoom,
						duration: 1200
					});
				}
			});
		} else {
			setSelectedData(props);
		}
	}

	// remove previous spiderified markers if they are visible
	const removeSpiderifiedMarkers = () => {
		// reset cluster styling
		if (clickedCluster.current) clickedCluster.current.classList.remove('halfopacity');

		// go over markers and remove them
		if (temporaryClusterMarkers.current.length) {
			temporaryClusterMarkers.current.forEach(marker => { marker.remove() });
			temporaryClusterMarkers.current = [];
		}
	};

	// marker spiderification placement
	const generateCircleParams = (count) => {
		const twoPi = Math.PI * 2;
		const circumference = SPIDER_CIRCUMFERENCE * (2 + count);
		const legLength = circumference / twoPi;
		const angleStep = twoPi / count;

		return mapTimes(count, (index) => {
			let angle = index * angleStep;
			let x = legLength * Math.cos(angle);
			let y = legLength * Math.sin(angle);
			return {
				x, y,
				angle: Math.atan2(y,x)*180.0/Math.PI + 90,
				//legLength: legLength,
				index: index
			};
		});
	}

	// Init map
	useEffect(() => {
		const initializeMap = ({ setMap, mapContainer }) => {
			const map = new mapboxgl.Map({
				container: mapContainer.current,
				style: 'mapbox://styles/greenpeacegmh/cln37eb5o001s01p9e9cu4vv8',
				center: DEFAULT_COORDS,
				zoom: DEFAULT_ZOOM,
				detectRetina: true,
				noWrap: true,
				continuousWorld: false,
				projection: 'equalEarth',
				attributionControl: false
			});
			setMap(map);

			// disable map rotation using right click + drag
			map.dragRotate.disable();
			// disable map rotation using touch rotation gesture
			map.touchZoomRotate.disableRotation();
			// re-add attribution
			map.addControl(new mapboxgl.AttributionControl({ compact: false }), 'bottom-left');

			// set default events
			map.on('click', (e) => {
				removeSpiderifiedMarkers();
			});
		};

		if (!map) initializeMap({ setMap, mapContainer });
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setMap, map]);

	// On categories change
	useEffect(() => {
		if (!activeCategories && !map) return;
		toggleLayers(activeCategories);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeCategories]);

	// Trigger on map load
	useEffect(() => {
		if (map) {
			map.on('load', () => { setMapLoaded(true); });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map]);

	// Load data and create layers
	useEffect(() => {
		if (mapLoaded && data) {
			addPointLayer(data);
			addOverlayLayer();
			setLayersLoaded(true);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, mapLoaded]);

	// Update sidebar width
	useEffect(() => {
		sidebarWidthLocal.current = sidebarWidth;
	}, [sidebarWidth]);

	// Trigger on current ID change
	useEffect(() => {
		// Change marker styling
		if (layersLoaded) {

			// Remove style from previous marker
			if (selectedDataPrev && selectedDataPrev.current !== null) {
				const prevSrcName = `${selectedDataPrev.current.ID}`;
				if (map.getSource(prevSrcName)) {
					map.setFeatureState({
						source: prevSrcName,
						id: selectedDataPrev.current.ID
					}, { 'clicked': false });
				}
			}

			// Add style to the current marker
			if (selectedData && selectedData.current !== null) {
				const srcName = `${selectedData.ID}`;
				if (map.getSource(srcName)) {
					map.setFeatureState({
						source: srcName,
						id: selectedData.ID
					}, { 'clicked': true });
				}
			}
		}
		selectedDataPrev.current = selectedData;
	}, [layersLoaded, selectedData, map]);

	// Render
	return (
		<div className="map-wrapper">
			<div ref={mapContainer} className="map-container" />
		</div>
	);
};

export default Map;